import React from 'react';
import type { RiskScoreDirection } from 'src/shared/components/risk-score-cell/riskScoreColour';

type RiskBarometerProps = {
    value: number | null;
    direction: RiskScoreDirection;
    lowLabel?: string;
    highLabel?: string;
};

const SEGMENTS_HIGHER_WORSE = [
    'bg-divergent-red-amber-green-5',
    'bg-divergent-red-amber-green-4',
    'bg-divergent-red-amber-green-3',
    'bg-divergent-red-amber-green-2',
    'bg-divergent-red-amber-green-1',
];

const SEGMENTS_HIGHER_BETTER = [
    'bg-divergent-red-amber-green-1',
    'bg-divergent-red-amber-green-2',
    'bg-divergent-red-amber-green-3',
    'bg-divergent-red-amber-green-4',
    'bg-divergent-red-amber-green-5',
];

const RiskBarometer = ({ value, direction, lowLabel = 'V. Low', highLabel = 'V. High' }: RiskBarometerProps) => {
    const segments = direction === 'higher-worse' ? SEGMENTS_HIGHER_WORSE : SEGMENTS_HIGHER_BETTER;
    const clampedValue = value !== null ? Math.max(0, Math.min(1, value)) : null;

    return (
        <div className='w-full'>
            <div className='relative'>
                {/* Bar */}
                <div data-testid='risk-barometer-bar' className='flex h-2 rounded-full overflow-hidden w-full'>
                    {clampedValue === null ? (
                        <div data-testid='risk-barometer-empty' className='flex-1 bg-grey-20 rounded' />
                    ) : (
                        segments.map((cls, i) => <div key={i} className={`flex-1 ${cls}`} />)
                    )}
                </div>
                {/* Tick markers */}
                {[0, 20, 40, 60, 80, 100].map(pct => (
                    <div
                        key={pct}
                        data-testid='risk-barometer-tick'
                        className='absolute top-full -translate-x-1/2 w-0.5 h-2 rounded-full bg-[#DEDEDE]'
                        style={{ left: `${pct}%` }}
                    />
                ))}
                {/* Cursor */}
                {clampedValue !== null && (
                    <div
                        data-testid='risk-barometer-pointer'
                        className='absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-4 w-1 rounded-full bg-[#3D3D3D]'
                        style={{ left: `${clampedValue * 100}%` }}
                    />
                )}
            </div>
            {/* Labels */}
            <div className='flex justify-between mt-3'>
                <span className='text-xs font-normal text-grey-80'>{lowLabel}</span>
                <span className='text-xs font-normal text-grey-80'>{highLabel}</span>
            </div>
        </div>
    );
};

export default RiskBarometer;
